import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { subDays } from "date-fns";

import FilterDimension from "../FilterDimension";
import { getAllChargingSites } from "../../api/ChargingSites";
import { getAllBiddingZones, getAllOrganizations } from "../../api/Resources";
import { useAuth } from "../../providers/AuthContext";

const MAX_SELECTED_SITES = 20;
const MAX_RANGE_DAYS = 366;
const BIDDING_ZONE_LABEL = "Bidding zone";
const OWNER_LABEL = "Owner";
const CHARGING_SITE_LABEL = "Charging site";

function toDateInputValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function parseDateInput(value) {
    if (!value) {
        return null;
    }
    const [y, m, d] = value.split("-").map(Number);
    if (!y || !m || !d) {
        return null;
    }
    return new Date(y, m - 1, d);
}

function toLocalDayRangeIso(startDate, endDate) {
    if (!startDate || !endDate) {
        return { startDateTime: null, endDateTime: null };
    }

    const startLocal = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        0,
        0,
        0,
        0
    );

    const endLocal = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
        23,
        59,
        59,
        999
    );

    return {
        startDateTime: startLocal.toISOString(),
        endDateTime: endLocal.toISOString(),
    };
}

function validateDateRange(startDate, endDate, t) {
    if (!startDate || !endDate) {
        return t("forecast.selectDateRange");
    }

    if (endDate < startDate) {
        return t("forecast.endAfterStart");
    }

    const diffMs = endDate.getTime() - startDate.getTime();
    const diffDays = diffMs / (24 * 60 * 60 * 1000);
    if (diffDays > MAX_RANGE_DAYS) {
        return t("forecast.rangeTooLong", { days: MAX_RANGE_DAYS });
    }

    return null;
}

function siteDisplayName(site) {
    return site.name || site.chargingSitePublicId;
}

function normalizeZoneName(value) {
    return String(value || "").trim().toLowerCase();
}

function enrichSitesWithBiddingZones(allSites, biddingZones) {
    if (!Array.isArray(allSites) || allSites.length === 0) {
        return [];
    }

    if (!Array.isArray(biddingZones) || biddingZones.length === 0) {
        return allSites;
    }

    const zoneByPublicId = new Map(
        biddingZones
            .filter((zone) => zone?.biddingZonePublicId)
            .map((zone) => [zone.biddingZonePublicId, zone])
    );

    const zoneByName = new Map(
        biddingZones
            .filter((zone) => zone?.biddingZoneName)
            .map((zone) => [normalizeZoneName(zone.biddingZoneName), zone])
    );

    return allSites.map((site) => {
        const zone =
            (site.biddingZonePublicId && zoneByPublicId.get(site.biddingZonePublicId)) ||
            (site.biddingZoneName && zoneByName.get(normalizeZoneName(site.biddingZoneName))) ||
            null;

        if (!zone) {
            return site;
        }

        return {
            ...site,
            biddingZonePublicId: site.biddingZonePublicId || zone.biddingZonePublicId,
            biddingZoneName: site.biddingZoneName || zone.biddingZoneName,
        };
    });
}

function siteMatchesZone(site, zonePublicId, zoneName) {
    if (!zonePublicId && !zoneName) {
        return true;
    }

    if (zonePublicId && site.biddingZonePublicId === zonePublicId) {
        return true;
    }

    if (zoneName && normalizeZoneName(site.biddingZoneName) === normalizeZoneName(zoneName)) {
        return true;
    }

    return false;
}

function getSiteOwnerPublicId(site) {
    return site?.owner?.organizationPublicId || "";
}

function siteMatchesOwner(site, ownerPublicId) {
    if (!ownerPublicId) {
        return true;
    }

    return getSiteOwnerPublicId(site) === ownerPublicId;
}

function buildSiteOptions(allSites, zonePublicId, zoneName, ownerPublicId) {
    return allSites
        .filter(
            (site) =>
                siteMatchesZone(site, zonePublicId, zoneName) &&
                siteMatchesOwner(site, ownerPublicId)
        )
        .map(siteDisplayName);
}

function resolveOwnerPublicId(organizations, ownerName) {
    if (!ownerName) {
        return null;
    }

    const org = organizations.find((o) => o.organizationName === ownerName);
    return org?.organizationPublicId || null;
}

function buildInitialDimension(t) {
    return [
        {
            label: BIDDING_ZONE_LABEL,
            options: [],
            selected: [],
            multiSelect: false,
            searchable: true,
            isActive: false,
        },
        {
            label: OWNER_LABEL,
            options: [],
            selected: [],
            multiSelect: false,
            searchable: true,
            isActive: false,
        },
        {
            label: CHARGING_SITE_LABEL,
            options: [],
            selected: [],
            multiSelect: true,
            searchable: true,
            isActive: false,
            maxSelectedOptions: MAX_SELECTED_SITES,
            maxSelectedOptionsMessage: t
                ? t("gridCost.maxSites", { count: MAX_SELECTED_SITES })
                : `You can select up to ${MAX_SELECTED_SITES} sites.`,
        },
    ];
}

export default function ForecastFollowUpFilters({
    onFiltersChange,
    onLoadingChange,
    onFetch,
    fetching = false,
    loadingData = false,
}) {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const initialEnd = new Date();
    const initialStart = subDays(initialEnd, 7);

    const [biddingZones, setBiddingZones] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [zonesError, setZonesError] = useState(null);
    const [sites, setSites] = useState([]);
    const enrichedSites = useMemo(
        () => enrichSitesWithBiddingZones(sites, biddingZones),
        [sites, biddingZones]
    );
    const [startDate, setStartDate] = useState(initialStart || subDays(new Date(), 7));
    const [endDate, setEndDate] = useState(initialEnd || new Date());
    const [dimension, setDimension] = useState(() => buildInitialDimension());
    const prevZoneNameRef = useRef("");
    const prevOwnerNameRef = useRef("");

    useEffect(() => {
        let cancelled = false;

        const loadZones = async () => {
            try {
                const token = await getAccessToken();
                const zones = (await getAllBiddingZones(token)) || [];
                if (cancelled) {
                    return;
                }

                const sortedZones = zones
                    .slice()
                    .sort((a, b) =>
                        (a.biddingZoneName || "").localeCompare(b.biddingZoneName || "", undefined, {
                            sensitivity: "base",
                        })
                    );

                setBiddingZones(sortedZones);
                setZonesError(null);
            } catch (err) {
                if (!cancelled) {
                    setZonesError(err.message);
                    toast.error(err.message);
                }
            }
        };

        const loadOrganizations = async () => {
            try {
                const token = await getAccessToken();
                const orgList = (await getAllOrganizations(token)) || [];
                if (cancelled) {
                    return;
                }

                setOrganizations(
                    orgList.slice().sort((a, b) =>
                        (a.organizationName || "").localeCompare(b.organizationName || "", undefined, {
                            sensitivity: "base",
                        })
                    )
                );
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.message);
                }
            }
        };

        const loadSites = async () => {
            try {
                onLoadingChange?.(true);
                const token = await getAccessToken();
                const siteList = await getAllChargingSites(token);

                if (!cancelled) {
                    setSites(
                        (siteList || []).slice().sort((a, b) =>
                            siteDisplayName(a).localeCompare(siteDisplayName(b), undefined, {
                                sensitivity: "base",
                            })
                        )
                    );
                }
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.message);
                }
            } finally {
                if (!cancelled) {
                    onLoadingChange?.(false);
                }
            }
        };

        loadZones();
        loadOrganizations();
        loadSites();

        return () => {
            cancelled = true;
        };
    }, [getAccessToken, onLoadingChange]);

    const biddingZoneOptions = useMemo(
        () => biddingZones.map((z) => z.biddingZoneName).filter(Boolean),
        [biddingZones]
    );

    const ownerOptions = useMemo(
        () => organizations.map((o) => o.organizationName).filter(Boolean),
        [organizations]
    );

    const selectedBiddingZoneName =
        dimension.find((d) => d.label === BIDDING_ZONE_LABEL)?.selected?.[0] || "";

    const selectedOwnerName = dimension.find((d) => d.label === OWNER_LABEL)?.selected?.[0] || "";

    const selectedOwnerPublicId = useMemo(
        () => resolveOwnerPublicId(organizations, selectedOwnerName),
        [organizations, selectedOwnerName]
    );

    const selectedBiddingZone = useMemo(
        () => biddingZones.find((z) => z.biddingZoneName === selectedBiddingZoneName) || null,
        [biddingZones, selectedBiddingZoneName]
    );

    const syncDimensionOptions = useCallback(
        (prev, zonePublicId, zoneName, ownerPublicId, autoSelectFilteredSites = false) => {
            const siteOptions = buildSiteOptions(
                enrichedSites,
                zonePublicId,
                zoneName,
                ownerPublicId
            );

            return prev.map((item) => {
                if (item.label === BIDDING_ZONE_LABEL) {
                    return {
                        ...item,
                        options: biddingZoneOptions,
                    };
                }

                if (item.label === OWNER_LABEL) {
                    return {
                        ...item,
                        options: ownerOptions,
                    };
                }

                if (item.label === CHARGING_SITE_LABEL) {
                    let selected = (item.selected || []).filter((name) => siteOptions.includes(name));

                    if (autoSelectFilteredSites && siteOptions.length > 0) {
                        selected = [...siteOptions];
                    }

                    return {
                        ...item,
                        options: siteOptions,
                        selected: selected.slice(0, MAX_SELECTED_SITES),
                        maxSelectedOptionsMessage: t("gridCost.maxSites", { count: MAX_SELECTED_SITES }),
                    };
                }

                return item;
            });
        },
        [enrichedSites, biddingZoneOptions, ownerOptions, t]
    );

    useEffect(() => {
        if (enrichedSites.length === 0 && biddingZoneOptions.length === 0 && ownerOptions.length === 0) {
            return;
        }

        setDimension((prev) => {
            const zoneName = prev.find((d) => d.label === BIDDING_ZONE_LABEL)?.selected?.[0] || "";
            const ownerName = prev.find((d) => d.label === OWNER_LABEL)?.selected?.[0] || "";
            const zone = biddingZones.find((z) => z.biddingZoneName === zoneName);
            const zonePublicId = zone?.biddingZonePublicId || null;
            const ownerPublicId = resolveOwnerPublicId(organizations, ownerName);
            const siteItem = prev.find((d) => d.label === CHARGING_SITE_LABEL);
            const shouldAutoFillFilteredSites =
                Boolean(zonePublicId || zoneName || ownerPublicId) &&
                !(siteItem?.selected?.length > 0);

            prevZoneNameRef.current = zoneName;
            prevOwnerNameRef.current = ownerName;

            return syncDimensionOptions(
                prev,
                zonePublicId,
                zoneName,
                ownerPublicId,
                shouldAutoFillFilteredSites
            );
        });
    }, [
        enrichedSites,
        biddingZoneOptions,
        ownerOptions,
        biddingZones,
        organizations,
        syncDimensionOptions,
    ]);

    const handleDimensionChange = useCallback(
        (nextDimension) => {
            if (!Array.isArray(nextDimension)) {
                return;
            }

            const nextZoneName =
                nextDimension.find((d) => d.label === BIDDING_ZONE_LABEL)?.selected?.[0] || "";
            const nextOwnerName = nextDimension.find((d) => d.label === OWNER_LABEL)?.selected?.[0] || "";
            const zoneChanged = nextZoneName !== prevZoneNameRef.current;
            const ownerChanged = nextOwnerName !== prevOwnerNameRef.current;
            prevZoneNameRef.current = nextZoneName;
            prevOwnerNameRef.current = nextOwnerName;

            const nextZone = biddingZones.find((z) => z.biddingZoneName === nextZoneName);
            const zonePublicId = nextZone?.biddingZonePublicId || null;
            const zoneName = nextZone?.biddingZoneName || nextZoneName || null;
            const ownerPublicId = resolveOwnerPublicId(organizations, nextOwnerName);

            setDimension((_prev) => {
                const cloned = nextDimension.map((item) => ({
                    ...item,
                    selected: [...(item.selected || [])],
                    options: [...(item.options || [])],
                }));

                const filterActive = Boolean(zonePublicId || zoneName || ownerPublicId);
                const autoSelect = (zoneChanged || ownerChanged) && filterActive;
                return syncDimensionOptions(
                    cloned,
                    zonePublicId,
                    zoneName,
                    ownerPublicId,
                    autoSelect
                );
            });
        },
        [biddingZones, organizations, syncDimensionOptions]
    );

    const selectedSiteNames = (
        dimension.find((d) => d.label === CHARGING_SITE_LABEL)?.selected || []
    ).slice(0, MAX_SELECTED_SITES);

    const sitePublicIds = useMemo(
        () =>
            selectedSiteNames
                .map((name) => enrichedSites.find((s) => siteDisplayName(s) === name)?.chargingSitePublicId)
                .filter(Boolean),
        [selectedSiteNames, enrichedSites]
    );

    const dateRangeError = useMemo(
        () => validateDateRange(startDate, endDate, t),
        [startDate, endDate, t]
    );

    const siteLimitError =
        (dimension.find((d) => d.label === CHARGING_SITE_LABEL)?.selected || []).length >
        MAX_SELECTED_SITES
            ? t("gridCost.selectNoMoreSites", { count: MAX_SELECTED_SITES })
            : null;

    const siteSelectionError =
        sitePublicIds.length === 0 ? t("forecast.selectSiteOrZone") : null;

    const aggregateByBiddingZone = Boolean(selectedBiddingZone?.biddingZonePublicId);

    const isValid = useMemo(
        () => Boolean(sitePublicIds.length > 0 && !dateRangeError && !siteLimitError),
        [sitePublicIds, dateRangeError, siteLimitError]
    );

    const { startDateTime, endDateTime } = useMemo(
        () => toLocalDayRangeIso(startDate, endDate),
        [startDate, endDate]
    );

    useEffect(() => {
        onFiltersChange?.({
            biddingZonePublicId: selectedBiddingZone?.biddingZonePublicId || "",
            biddingZoneName: selectedBiddingZone?.biddingZoneName || "",
            aggregateByBiddingZone,
            sitePublicIds,
            siteNames: selectedSiteNames,
            startDateTime,
            endDateTime,
            isValid,
            dateRangeError,
            siteLimitError,
            siteSelectionError,
        });
    }, [
        selectedBiddingZone,
        selectedBiddingZoneName,
        aggregateByBiddingZone,
        sitePublicIds,
        selectedSiteNames,
        startDateTime,
        endDateTime,
        startDate,
        endDate,
        isValid,
        dateRangeError,
        siteLimitError,
        siteSelectionError,
        onFiltersChange,
    ]);

    const filteredSiteCount =
        enrichedSites.length > 0 &&
        (selectedBiddingZone || selectedOwnerPublicId)
            ? buildSiteOptions(
                  enrichedSites,
                  selectedBiddingZone?.biddingZonePublicId || null,
                  selectedBiddingZone?.biddingZoneName || null,
                  selectedOwnerPublicId
              ).length
            : 0;

    const filteredSiteLimitWarning =
        (selectedBiddingZone || selectedOwnerName) && filteredSiteCount > MAX_SELECTED_SITES
            ? t("forecast.filterLimit", { count: filteredSiteCount, max: MAX_SELECTED_SITES })
            : null;

    const filteredHasNoSitesWarning =
        (selectedBiddingZone || selectedOwnerName) &&
        enrichedSites.length > 0 &&
        filteredSiteCount === 0
            ? selectedBiddingZone && selectedOwnerName
                ? t("forecast.noSitesZoneOwner", {
                      zone: selectedBiddingZone.biddingZoneName,
                      owner: selectedOwnerName,
                  })
                : selectedBiddingZone
                  ? t("forecast.noSitesZone", { zone: selectedBiddingZone.biddingZoneName })
                  : t("forecast.noSitesOwner", { owner: selectedOwnerName })
            : null;

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg">
            <FilterDimension
                dimension={dimension}
                setdimension={handleDimensionChange}
                index={0}
                allowDeleteDimension={false}
            />

            {zonesError ? <p className="text-xs text-red-600 mt-3">{zonesError}</p> : null}

            <div className="mt-4 flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1 text-sm">
                    <span className="text-mute-600">{t("forecast.fromDate")}</span>
                    <input
                        type="date"
                        className="border border-mute-300 rounded px-2 py-1.5 text-sm"
                        value={toDateInputValue(startDate)}
                        onChange={(e) => setStartDate(parseDateInput(e.target.value) || startDate)}
                    />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                    <span className="text-mute-600">{t("forecast.toDate")}</span>
                    <input
                        type="date"
                        className="border border-mute-300 rounded px-2 py-1.5 text-sm"
                        value={toDateInputValue(endDate)}
                        onChange={(e) => setEndDate(parseDateInput(e.target.value) || endDate)}
                    />
                </label>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={fetching || loadingData || !isValid}
                    onClick={onFetch}
                >
                    {fetching ? t("common.loading") : t("common.show")}
                </button>
            </div>

            <p className="text-xs text-mute-500 mt-3">
                {t("forecast.filterHelp")}
            </p>
            {filteredHasNoSitesWarning ? (
                <p className="text-xs text-amber-700 mt-1">{filteredHasNoSitesWarning}</p>
            ) : null}
            {filteredSiteLimitWarning ? (
                <p className="text-xs text-amber-700 mt-1">{filteredSiteLimitWarning}</p>
            ) : null}
            {dateRangeError ? <p className="text-xs text-red-600 mt-1">{dateRangeError}</p> : null}
            {siteSelectionError ? <p className="text-xs text-red-600 mt-1">{siteSelectionError}</p> : null}
            {siteLimitError ? <p className="text-xs text-red-600 mt-1">{siteLimitError}</p> : null}
        </div>
    );
}
